import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataItem } from '../../../../@types/DataItem';
import { AlertWithTimestamp } from '../../../../components/AlertWithTimestamp';
import { checkIfIsLoading } from '../../../../utils';
import { extractApiErrorMessage } from '../../../../utils/extractApiErrorMessage';
import { listNetworkBrands, listNetworkFamilies, listNetworkFieldOptions, listNetworkProducts, listNetworkSuppliers } from '../services';
import { useNetworkPricesStore } from '../NetworkPrices.store';
import { NETWORK_CATEGORY_KEYS, NetworkCategoryKey, NetworkFilterKey, NetworkFilterOptions, NetworkOptionsCascade, NetworkOptionsLookup } from '../types';
import { getNetworkCascadeSignature, getNetworkCascadingParams } from '../utils';

export const NETWORK_FILTER_OPTIONS_QUERY_KEY = 'ipa.network-prices.filter-options';

type OptionsLoader = (query: string, cascade: NetworkOptionsCascade, lookup?: NetworkOptionsLookup) => Promise<DataItem[]>;

const loadCategoryOptions =
  (field: NetworkCategoryKey): OptionsLoader =>
  (query, cascade) =>
    listNetworkFieldOptions(field, query, cascade);

const OPTIONS_LOADER_BY_KEY: Record<NetworkFilterKey, OptionsLoader> = {
  productIds: listNetworkProducts,
  productFamilyIds: listNetworkFamilies,
  productBrand: listNetworkBrands,
  supplier: listNetworkSuppliers,
  ...(Object.fromEntries(NETWORK_CATEGORY_KEYS.map(key => [key, loadCategoryOptions(key)])) as Record<NetworkCategoryKey, OptionsLoader>),
};

const EMPTY_VALUE: string[] = [];

// Options of one filter chip: loaded only while it is open; product, family and categories narrow each other (cascade).
export const useNetworkFilterOptions = (key: NetworkFilterKey): NetworkFilterOptions => {
  const filters = useNetworkPricesStore(state => state.filters);
  const filterResetVersion = useNetworkPricesStore(state => state.filterResetVersion);
  const setFilter = useNetworkPricesStore(state => state.setFilter);
  const value = filters[key] ?? EMPTY_VALUE;

  const [isOpen, setIsOpen] = useState(false);
  const search = useNetworkPricesStore(state => state.filterSearch[key] ?? '');
  const setFilterSearch = useNetworkPricesStore(state => state.setFilterSearch);
  const [pendingPastes, setPendingPastes] = useState({ resetVersion: -1, count: 0 });
  const pasteSession = useRef(0);
  // Selected options keep their label in the chip even when the current search does not return them.
  const [selectedOptions, setSelectedOptions] = useState<DataItem[]>([]);

  const cascade = useMemo(() => getNetworkCascadingParams(filters, key), [filters, key]);
  const loadOptions = OPTIONS_LOADER_BY_KEY[key];

  const { data: options = [], fetchStatus } = useQuery({
    queryKey: [NETWORK_FILTER_OPTIONS_QUERY_KEY, key, search, getNetworkCascadeSignature(cascade)],
    queryFn: () => loadOptions(search, cascade),
    enabled: isOpen,
    keepPreviousData: true,
  });

  const data = useMemo(
    () => _.uniqBy([...selectedOptions.filter(option => value.includes(option.value)), ...options], 'value'),
    [options, selectedOptions, value],
  );

  const onSearch = useMemo(
    () =>
      _.debounce((query: string) => {
        if (useNetworkPricesStore.getState().filterResetVersion === filterResetVersion) setFilterSearch(key, query);
      }, 500),
    [filterResetVersion, key, setFilterSearch],
  );

  useEffect(() => () => onSearch.cancel(), [onSearch]);
  useEffect(
    () => () => {
      pasteSession.current += 1;
    },
    [],
  );

  const onOpen = useCallback(() => setIsOpen(true), []);

  const onClose = useCallback(() => {
    setIsOpen(false);
    onSearch.cancel();
    setFilterSearch(key, '');
  }, [key, onSearch, setFilterSearch]);

  const onChange = useCallback(
    (values: string[]) => {
      setSelectedOptions(previous => _.uniqBy([...previous, ...data], 'value').filter(option => values.includes(option.value)));
      setFilter(key, values);
    },
    [data, key, setFilter],
  );

  const onClean = useCallback(() => {
    onSearch.cancel();
    pasteSession.current += 1;
    setPendingPastes({ resetVersion: -1, count: 0 });
    setFilterSearch(key, '');
    setFilter(key, []);
  }, [key, onSearch, setFilter, setFilterSearch]);

  const onConfirmPasteValues = useCallback(
    async (text: string) => {
      if (!text) return;

      const session = pasteSession.current;
      const resetVersion = useNetworkPricesStore.getState().filterResetVersion;
      const isCurrent = () => session === pasteSession.current && resetVersion === useNetworkPricesStore.getState().filterResetVersion;
      setPendingPastes(previous => ({ resetVersion, count: previous.resetVersion === resetVersion ? previous.count + 1 : 1 }));
      try {
        const results = await loadOptions(text, cascade, { isPaste: true });
        if (!isCurrent() || !results.length) return;

        // Read the selection after the request: it may have changed while the codes were loading.
        const currentValue = useNetworkPricesStore.getState().filters[key] ?? EMPTY_VALUE;
        setSelectedOptions(previous => _.uniqBy([...previous, ...results], 'value'));
        setFilter(key, _.uniq([...currentValue, ...results.map(option => option.value)]));
      } catch (error: unknown) {
        if (isCurrent()) AlertWithTimestamp({ message: extractApiErrorMessage(error) ?? 'Erro ao buscar os códigos colados' });
      } finally {
        if (isCurrent()) setPendingPastes(previous => ({ ...previous, count: previous.count - 1 }));
      }
    },
    [cascade, key, loadOptions, setFilter],
  );

  return {
    data,
    value,
    search,
    isLoading: checkIfIsLoading(fetchStatus) || (pendingPastes.resetVersion === filterResetVersion && pendingPastes.count > 0),
    onSearch,
    onOpen,
    onClose,
    onChange,
    onClean,
    onConfirmPasteValues,
  };
};
